import {
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  OnInit,
  HostBinding,
  Output,
  EventEmitter,
  OnDestroy,
  inject
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CoreActionDisplayFormErrors, CoreUtilsService } from '@bepatient/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { ThemeService } from '../../../services/theme.service';
import {
  ConstraintTypesV3,
  QuestionV3ControlTypes
} from '../../../dynamic-form-v3/classes/enum';
import { QuestionV3Constraint } from '../../models/dynamic-form-v3.models';

@Component({
  selector: 'bepatient-question-title-v3',
  templateUrl: './question-title.component.html',
  styleUrls: ['./question-title.component.scss'],
  standalone: false
})
export class QuestionTitleV3
  implements AfterViewInit, OnChanges, OnInit, OnDestroy
{
  private sanitizer = inject(DomSanitizer);
  private store$ = inject<Store<any>>(Store);
  private coreUtilsService = inject(CoreUtilsService);
  private themeService = inject(ThemeService);
  private cdr = inject(ChangeDetectorRef);

  @Input() title: string;
  @Input() color: string;
  @Input() constraints: QuestionV3Constraint[];
  @Input() infos: string[];
  @Input() errors: string[];
  @Input() isBlur: boolean;
  @Input() css: any;
  @Input() type: any;
  @Input() key: any;
  @Input() value: any;
  @Input() dirty: boolean;
  @Input() display: boolean;
  @Input() formGroup: any;
  @Input() hasCondition: boolean;

  @Output() resetEvent = new EventEmitter<boolean>();

  @ViewChild('afterTitle', { read: ElementRef, static: true })
  afterTitle: ElementRef;
  @ViewChild('optionalRes', { read: ElementRef, static: true })
  optionalRes: ElementRef;
  isRequired: boolean;
  isSlider: boolean;
  safeTitle: SafeHtml;
  formCurrentPageStatus: any;
  errMsgField: any;

  errorMsg: string = void 0;

  private rawCss: any;
  private themeSub?: Subscription;

  ngOnInit(): void {
    this.isSlider =
      this.type === QuestionV3ControlTypes.NUMBER_RANGE ? true : false;
    this.isRequired = this.checkIsRequired(this.constraints);

    // Keep the raw css so re-adjustment on theme change is computed from the
    // original (never from an already-adjusted value).
    this.rawCss = this.css;
    this.applyThemeColors();

    // Re-adjust inline colors on every theme / high-contrast toggle so the
    // title stays readable without a page reload.
    this.themeSub = this.themeService.themeChange$.subscribe(() => {
      this.applyThemeColors();
      this.cdr.markForCheck();
    });
  }

  /**
   * Re-derives the theme-adjusted title HTML and css from the raw inputs.
   */
  private applyThemeColors(): void {
    const adjustedTitle =
      this.coreUtilsService.adjustInlineColorsForTheme(this.title) ??
      this.title ??
      '';
    this.safeTitle = this.sanitizer.bypassSecurityTrustHtml(adjustedTitle);
    this.css =
      this.coreUtilsService.adjustCssObjectColorsForTheme(this.rawCss) ??
      this.rawCss;
  }

  ngAfterViewInit(): void {
    this.isBlurColorChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.checkQuestionError();
    setTimeout(() => {
      const reqConstraint = this.constraints.find(
        c => c.type == ConstraintTypesV3.REQUIRED
      );
      if (this.hasCondition) {
        // isRequired will be dynamic for conditional question
        // isRequired: true => constraint[requiredIndex].conditional is true
        // Note: required constraints (should always be true from webservice)
        if (reqConstraint && reqConstraint.conditional) {
          this.isRequired = reqConstraint.conditional;
        }
        this.checkQuestionError();
      }
    });
  }

  /**
   * Get form control errors
   */
  checkQuestionError() {
    if (this.formGroup) {
      const inputControl = this.formGroup.get(this.key);
      const formControlErrors = inputControl?.errors || void 0;
      (formControlErrors &&
        (this.value || inputControl?.dirty || this.hasCondition) &&
        inputControl?.touched &&
        Object.keys(formControlErrors).map(key => {
          this.errorMsg = this.getErrorMessage(key) || void 0;
        })) ||
        (this.errorMsg = void 0);
      /**
       * For those errors which can occurs even after question condition success, need to check again
       * This normally happens when syncWithCustomValidators method executes
       * */
      // Checking required error message
      if (
        formControlErrors &&
        (!this.value || this.value == undefined) &&
        inputControl?.dirty &&
        inputControl?.touched
      ) {
        this.errorMsg = this.getErrorMessage(ConstraintTypesV3.REQUIRED);
      }
      // Checking pattern error message
      if (formControlErrors && formControlErrors.pattern) {
        this.errorMsg = this.getErrorMessage(ConstraintTypesV3.PATTERN);
      }
    }
  }

  /**
   * Get error message using the constraint type key
   * @param constraintTypekey
   * @returns
   */
  getErrorMessage(constraintTypekey: string): string {
    return (
      this.constraints.find(c => c.type === constraintTypekey)?.errorMessage ||
      void 0
    );
  }

  isBlurColorChange(): void {
    if (this.errors && this.errors.length > 0) {
      this.afterTitle.nativeElement.style.background =
        'var(--ion-color-danger)';
      this.optionalRes.nativeElement.style.color = 'var(--ion-color-danger)';
    } else {
      if (this.isBlur || this.isBlur === undefined || !this.errors) {
        this.afterTitle.nativeElement.style.background =
          'var(--ion-text-color)';
        this.optionalRes.nativeElement.style.color = 'var(--ion-text-color)';
      } else {
        if (this.errors === undefined && !this.isBlur) {
          this.afterTitle.nativeElement.style.background =
            'var(--ion-text-color)';
        } else {
          this.optionalRes.nativeElement.style.color = 'var(--ion-text-color)';
          this.afterTitle.nativeElement.style.background = this.color;
        }
      }
    }
  }

  resetHandle(): void {
    this.resetEvent.emit(true);
  }

  ngOnDestroy(): void {
    this.store$.dispatch(new CoreActionDisplayFormErrors(false));
    this.themeSub?.unsubscribe();
  }

  private checkIsRequired(constraints: QuestionV3Constraint[]): boolean {
    return constraints.findIndex(value => value.type === 'required') >= 0;
  }
}
